import type { AdminProduct, AdminProductVariant } from '@/api/productApi';

export type StockStatus = 'in' | 'low' | 'out';

export interface AdminProductRow {
  product: AdminProduct;
  variant: AdminProductVariant;
  cost: number | null;
  quantity: number;
  status: StockStatus;
}

interface ProductRowFilters {
  search: string;
  categoryName?: string;
  brandName?: string;
  stockStatus: StockStatus | '';
}

interface PaginationRange {
  start: number;
  end: number;
}

export const getStockStatus = (quantity: number): StockStatus => {
  if (quantity === 0) return 'out';
  return quantity <= 5 ? 'low' : 'in';
};

export const getLatestCost = (variant: AdminProductVariant): number | null => {
  const latestReceipt = [...(variant.purchaseReceipts ?? [])]
    .sort((first, second) => second.receiptId - first.receiptId)[0];
  return latestReceipt?.unitCost ?? null;
};

export const mapProductsToRows = (products: AdminProduct[]): AdminProductRow[] =>
  products.flatMap((product) => (product.productVariants ?? []).map((variant) => {
    const quantity = variant.inventory?.quantity ?? 0;
    return {
      product,
      variant,
      quantity,
      cost: getLatestCost(variant),
      status: getStockStatus(quantity),
    };
  }));

const normalizeSearch = (value: string): string => value.trim().toLocaleLowerCase('vi-VN');

export const filterProductRows = (
  rows: AdminProductRow[],
  filters: ProductRowFilters,
): AdminProductRow[] => {
  const normalizedSearch = normalizeSearch(filters.search);
  return rows.filter(({ product, variant, status }) => {
    if (filters.categoryName && product.category?.name !== filters.categoryName) return false;
    if (filters.brandName && product.brand?.name !== filters.brandName) return false;
    if (filters.stockStatus && status !== filters.stockStatus) return false;
    if (!normalizedSearch) return true;
    return normalizeSearch(`${product.name} ${variant.id} ${variant.versionName} ${variant.color}`)
      .includes(normalizedSearch);
  });
};

export const getPaginationRange = (
  currentPage: number,
  pageSize: number,
  totalElements: number,
  pageDataLength: number,
): PaginationRange => {
  if (totalElements === 0 || pageDataLength === 0) return { start: 0, end: 0 };
  const start = (currentPage - 1) * pageSize + 1;
  return {
    start,
    end: Math.min(start + pageDataLength - 1, totalElements),
  };
};
